/** @jsxImportSource ./simboluri-jsx */
import { textVerdict } from "./elemente";
import { useEffect, useMemo, useRef, useState } from "react";
import { ArmareLamela, ArmarePeretiResult } from "./tipuri";
import TitluVerificare, { useCatalogVerificari } from "./Verificare";
import { Focus } from "./legaturi";
import type { Cheie } from "../i18n";
import { t, useT } from "../i18n";
import SectiuneLamela, { VedereLamelaCotata } from "./SectiuneLamela";
import { Ceapa3D } from "./StepArmareStalpi";

// Pasul 14 — lamelele: elementele verticale cu latura lungă ≥ 4·b, armate ca
// pereți structurali (`armare_lamele.py`). Pasul 15 nu le mai vede, deci aici
// e singurul loc unde se citesc armarea și verificările lor: pe poziție (baza,
// zona critică, Ω și ε), pe nivel (capetele, inima, etrierii) și, pe nivelul
// ales, fiecare verificare cu E, R și articolul ei.

function nr(v: number | undefined | null, zecimale = 1): string {
  if (v === undefined || v === null || Number.isNaN(v)) return "—";
  return v.toFixed(zecimale).replace(".", ",");
}

function verdictClass(v: string): string {
  if (v.startsWith("SE VERIFICA")) return "st-ok";
  if (v.startsWith("ATENTIE")) return "st-warn";
  return "st-bad";
}

const ZONA: Record<ArmareLamela["zona"], Cheie> = {
  A: "struct.arl.zona_a",
  B: "struct.arl.zona_b",
  infrastructura: "struct.arl.zona_infra",
};

/** Lamelele Pasului 14. Cu `element` (pagina unei lamele) se restrânge la
 *  stiva ei — rândul stivei, nivelurile ei și detaliul lamelei — fără
 *  rezumatul pasului, care e al tuturor. */
export default function LameleArmate({
  r,
  focus,
  element,
}: {
  r: ArmarePeretiResult;
  focus?: Focus;
  element?: string;
}) {
  useT();
  const catalog = useCatalogVerificari();
  const stiva = element
    ? (r.lamele ?? []).find((x) => x.element === element)?.stack ?? null : null;
  const lamele = useMemo(
    () =>
      [...(r.lamele ?? [])]
        .filter((x) => !stiva || x.stack === stiva)
        .sort(
          (a, b) =>
            a.stack.localeCompare(b.stack, undefined, { numeric: true }) ||
            a.etaj - b.etaj
        ),
    [r, stiva]
  );
  const [sel, setSel] = useState<string>(element ?? "");
  const detaliu = useRef<HTMLDivElement>(null);
  // linkul din tabloul verificărilor poartă id-ul de plan al nivelului
  // („ST12.3"): rândul se alege și pagina coboară la el
  useEffect(() => {
    if (!focus?.ref) return;
    const gasit = lamele.find((x) => x.element === focus.ref);
    if (gasit) {
      setSel(gasit.element);
      detaliu.current?.scrollIntoView?.({ block: "start" });
    }
  }, [focus?.n, focus?.ref, lamele]);
  if (!lamele.length) return null;
  const l = lamele.find((x) => x.element === sel) ?? lamele[0];
  const par = r.parametri_lamele;
  const verdicte = Object.entries(r.verdicte_lamele ?? {})
    .filter(([, n]) => n > 0)
    .map(([v, n]) => `${n} ${v}`)
    .join(" · ");

  return (
    <>
      {!element && (
      <div className="st-findings">
        <h3>{t("struct.arl.titlu")}</h3>
        <div className="sg-muted" style={{ marginBottom: 8 }}>
          {t("struct.arl.intro", { raport: nr(par?.raport_lamela ?? 4, 0) })}
        </div>
        <div className="st-cards">
          <div className="st-card st-card-main">
            <div className="st-card-label">{t("struct.arl.card_nr")}</div>
            <div className="st-card-value">{r.nr_lamele ?? lamele.length}</div>
            <div className="st-card-sub">{verdicte || "—"}</div>
          </div>
          <div className="st-card">
            <div className="st-card-label">{t("struct.arl.card_regim")}</div>
            <div className="st-card-value">{par?.clasa_ductilitate ?? "—"}</div>
            <div className="st-card-sub">
              {t("struct.arl.regim_sub", {
                q: nr(par?.q, 2), km: nr(par?.k_M, 2), kv: nr(par?.k_V, 1),
                grd: nr(par?.gamma_rd, 2),
              })}
            </div>
          </div>
          <div className="st-card">
            <div className="st-card-label">{t("struct.arl.card_materiale")}</div>
            <div className="st-card-value">{nr(r.otel_lamele_kg, 0)} kg</div>
            <div className="st-card-sub">
              {t("struct.arl.materiale_sub", { beton: nr(r.beton_lamele_m3, 1) })}
            </div>
          </div>
          {r.carcase_lamele && (
            <div className="st-card">
              <div className="st-card-label">{t("struct.arl.card_carcase")}</div>
              <div className="st-card-value">{r.carcase_lamele.nr_carcase_distincte}</div>
              <div className="st-card-sub">
                {t("struct.arl.carcase_sub", {
                  n: r.carcase_lamele.nr_carcase_catalog,
                  surplus: nr(r.carcase_lamele.surplus_otel_pct, 1),
                  ref: r.carcase_lamele.pozitii_refuzate.length,
                })}
              </div>
            </div>
          )}
        </div>
      </div>
      )}

      <div className="st-findings">
        <h3>{t("struct.arl.pozitii")}</h3>
        <div style={{ overflowX: "auto" }}>
          <table className="sg-table">
            <thead>
              <tr>
                <th>{t("struct.arl.th_pozitie")}</th>
                <th>{t("struct.arl.th_sectiune")}</th>
                <th>{t("struct.arl.th_inaltime")}</th>
                <th>{t("struct.arl.th_zona_critica")}</th>
                <th>{t("struct.arl.th_omega")}</th>
                <th>{t("struct.arl.th_taietoare_baza")}</th>
                <th>{t("struct.arp.th_verdict")}</th>
              </tr>
            </thead>
            <tbody>
              {(r.stive_lamele ?? []).filter((s) => !stiva || s.stack === stiva)
                .map((s) => (
                <tr key={s.stack}>
                  <td>
                    <strong>{s.stack}</strong>{" "}
                    <span className="sg-muted">
                      {s.baza ? t("struct.arl.baza", { el: s.baza }) : ""}
                    </span>
                  </td>
                  <td className="sg-mono">
                    {nr(s.l_w_m, 2)} × {nr(s.b_cm / 100, 2)}
                  </td>
                  <td className="sg-mono">
                    {nr(s.H_w_m, 1)}
                    {s.scurt ? <span className="sg-muted"> {t("struct.arl.scurt")}</span> : null}
                  </td>
                  <td className="sg-mono">
                    {nr(s.l_cr_m, 2)}{" "}
                    <span className="sg-muted">({s.zona_A.join(", ") || "—"})</span>
                  </td>
                  <td className="sg-mono">
                    {nr(s.omega, 2)} / {nr(s.epsilon, 2)}
                  </td>
                  <td className="sg-mono">{nr(s.V_Ed0_kn, 0)}</td>
                  <td><span className={verdictClass(s.verdict)}>{textVerdict(s.verdict)}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="st-findings">
        <h3>{t("struct.arl.niveluri")}</h3>
        <div style={{ overflowX: "auto" }}>
          <table className="sg-table">
            <thead>
              <tr>
                <th>{t("struct.arl.th_element")}</th>
                <th>{t("struct.arl.th_zona")}</th>
                <th>{t("struct.arl.th_capete")}</th>
                <th>{t("struct.arl.th_etrieri")}</th>
                <th>{t("struct.arl.th_inima")}</th>
                <th>{t("struct.arp.otel_kg")}</th>
                <th>{t("struct.arp.th_verdict")}</th>
              </tr>
            </thead>
            <tbody>
              {lamele.map((x) => {
                const c = x.armare.capete;
                const e = c.etrieri;
                const activ = x.element === l.element;
                return (
                  <tr
                    key={x.element}
                    onClick={() => setSel(x.element)}
                    style={{
                      cursor: "pointer",
                      background: activ ? "var(--sg-bg-alt, #e2f4f7)" : undefined,
                    }}
                  >
                    <td>
                      <strong>{x.element}</strong>{" "}
                      <span className="sg-muted">
                        ({x.nivel}, {nr(x.t_cm, 0)} × {nr(x.L_cm, 0)} cm)
                      </span>
                    </td>
                    <td>{t(ZONA[x.zona])}</td>
                    <td className="sg-mono">
                      2 × {c.n}Ø{c.phi_mm}{" "}
                      <span className="sg-muted">
                        ({nr(c.l_cm, 0)} cm, ρ {nr(c.rho_pct, 2)}%)
                      </span>
                    </td>
                    <td className="sg-mono">
                      Ø{e.phi_mm}/{nr(e.pas_cm, 1)}{" "}
                      <span className="sg-muted">({nr(e.l_confinat_cm, 0)} cm)</span>
                    </td>
                    <td className="sg-mono">
                      Ø{x.armare.vertical.phi_mm}/{x.armare.vertical.pas_mm} + Ø
                      {x.armare.orizontal.phi_mm}/{x.armare.orizontal.pas_mm}
                    </td>
                    <td className="sg-mono">{nr(x.otel_kg, 0)}</td>
                    <td><span className={verdictClass(x.verdict)}>{textVerdict(x.verdict)}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="sg-muted" style={{ marginTop: 6 }}>{t("struct.arl.click")}</div>
      </div>

      <div className="st-findings" ref={detaliu}>
        <h3>{t("struct.arl.detaliu", { el: l.element, nivel: l.nivel })}</h3>
        <div className="sg-muted" style={{ marginBottom: 8 }}>
          {t("struct.arl.detaliu_sub", {
            zona: t(ZONA[l.zona]),
            om: nr(l.eforturi?.omega ?? null, 2),
            eps: nr(l.eforturi?.epsilon ?? null, 2),
            xu: nr(l.eforturi?.x_u_cm ?? null, 0),
            lim: nr(l.eforturi?.x_u_lim_cm ?? null, 0),
            nud: nr(l.eforturi?.nu_d ?? null, 2),
          })}
        </div>
        <div style={{ overflowX: "auto" }}>
          <table className="sg-table">
            <thead>
              <tr>
                <th>{t("struct.arp.th_verificarea")}</th>
                <th>{t("struct.arp.th_e_adoptat")}</th>
                <th>{t("struct.arp.th_r_limita")}</th>
                <th>R/E</th>
                <th>{t("struct.arp.th_verdict")}</th>
              </tr>
            </thead>
            <tbody>
              {l.verificari.map((v, i) => (
                <tr key={i}>
                  <td>
                    <TitluVerificare catalog={catalog} pas="armare_pereti"
                                     nume={v.nume} grupa={v.grupa}
                                     text={v.nume} aratNumele={false}
                                     aratPasul={false} />
                    {v.referinta && (
                      <div className="sg-muted" style={{ fontSize: 11 }}>{v.referinta}</div>
                    )}
                  </td>
                  <td className="sg-mono">
                    {typeof v.E === "number" ? nr(v.E, 2) : v.E} {v.unitate ?? ""}
                  </td>
                  <td className="sg-mono">
                    {typeof v.R === "number" ? nr(v.R, 2) : v.R} {v.unitate ?? ""}
                  </td>
                  <td className="sg-mono">{v.RE == null ? "—" : nr(v.RE, 2)}</td>
                  <td><span className={verdictClass(v.verdict)}>{textVerdict(v.verdict)}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* --- 4. Armarea aleasă + desenele, pe pagina lamelei ------------
          Aceeași secțiune ca pe pagina peretelui: secțiunea cotată, elevația
          stivei, barele cu mărcile lor și «ceapa» P–M–M a secțiunii adoptate,
          cu punctele verificate pe ea. */}
      {element && (
        <div className="st-findings" data-testid="lamela-armarea-aleasa">
          <h3>{t("struct.arp.4_armarea_aleasa")}</h3>
          <div className="sg-muted" style={{ marginBottom: 8 }}>
            {t("struct.lam.s4_explicatie", { el: l.element, zona: t(ZONA[l.zona]) })}
          </div>
          <div style={{ display: "flex", gap: 18, flexWrap: "wrap", alignItems: "flex-start" }}>
            <div style={{ flex: "1 1 420px", minWidth: 0 }}>
              <SectiuneLamela l={l} cCm={par?.c_cm ?? 4} />
            </div>
            <div style={{ flex: "1 1 320px", minWidth: 0, maxWidth: 560 }}>
              <VedereLamelaCotata nivele={lamele} selectat={l.element}
                                  stiva={(r.stive_lamele ?? []).find((s) => s.stack === l.stack) ?? null}
                                  cCm={par?.c_cm ?? 4} inaltimeMax={420} />
            </div>
          </div>
          <TabelBareLamela l={l} />
          {l.suprafata && l.puncte && (
            <div style={{ marginTop: 12 }}>
              <Ceapa3D n={{ suprafata: l.suprafata, puncte: l.puncte }} />
              <div className="sg-muted" style={{ fontSize: 12, marginTop: 6 }}>
                {t("struct.ars.ceapa_legenda", {
                  b: nr(l.t_cm, 0), h: nr(l.L_cm, 0),
                  n: 2 * l.armare.capete.n, phi: l.armare.capete.phi_mm,
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {!element
        && ((r.nu_se_aplica_lamele?.length ?? 0) > 0 || (par?.limite?.length ?? 0) > 0) && (
        <div className="st-findings">
          <h3>{t("struct.arl.nu_se_aplica")}</h3>
          <ul className="sg-muted">
            {(r.nu_se_aplica_lamele ?? []).map((x, i) => (
              <li key={i}>
                <strong>{x.nume}</strong> — {x.motiv}{" "}
                <span style={{ fontSize: 11 }}>({x.referinta})</span>
              </li>
            ))}
          </ul>
          {(par?.limite?.length ?? 0) > 0 && (
            <>
              <div className="sg-muted" style={{ marginTop: 6 }}>
                <strong>{t("struct.arl.limite")}</strong>
              </div>
              <ul className="sg-muted">
                {(par?.limite ?? []).map((x, i) => <li key={i}>{x}</li>)}
              </ul>
            </>
          )}
        </div>
      )}
    </>
  );
}

/** Marca scrisă de pas pe un grup de bare, în forma din «Componente»:
 *  B pentru bare, E pentru etrieri și agrafe; «—» pe o rulare fără registru. */
function marca(cat: "B" | "E", tip?: string): string {
  return tip ? `${cat}${tip}` : "—";
}

/** Barele lamelei, cu rolul, numărul, diametrul, pasul și marca fiecărui
 *  grup — tabelul de armare al nivelului. */
function TabelBareLamela({ l }: { l: ArmareLamela }) {
  useT();
  const a = l.armare;
  const e = a.capete.etrieri;
  const randuri: [Cheie, string, string, string][] = [
    ["struct.lam.rol_capete",
     `2 × ${a.capete.n}Ø${a.capete.phi_mm}`,
     t("struct.lam.unde_capete", { l: nr(a.capete.l_cm, 0), rho: nr(a.capete.rho_pct, 2) }),
     marca("B", a.capete.tip_armare)],
    ["struct.lam.rol_etrieri",
     `Ø${e.phi_mm}/${nr(e.pas_cm, 1)} cm`,
     t("struct.lam.unde_etrieri", { a: nr(e.ochi_cm?.[0], 1), b: nr(e.ochi_cm?.[1], 1),
                                    n: e.nr_pe_capat, lconf: nr(e.l_confinat_cm, 0) }),
     marca("E", e.tip_armare)],
    ...(e.agrafe_pe_set
      ? [["struct.lam.rol_agrafe_conf",
          `${2 * e.nr_pe_capat * e.agrafe_pe_set} × Ø${e.phi_mm}`,
          t("struct.lam.unde_agrafe_conf", { k: e.agrafe_pe_set }),
          marca("E", e.tip_armare_agrafe)] as [Cheie, string, string, string]]
      : []),
    ["struct.lam.rol_vertical",
     `${a.vertical.nr_bare}Ø${a.vertical.phi_mm}/${a.vertical.pas_mm} mm`,
     t("struct.lam.unde_inima", { rho: nr(a.vertical.rho_pct, 2) }),
     marca("B", a.vertical.tip_armare)],
    ["struct.lam.rol_orizontal",
     `${a.orizontal.nr_bare}Ø${a.orizontal.phi_mm}/${a.orizontal.pas_mm} mm`,
     t("struct.lam.unde_inima", { rho: nr(a.orizontal.rho_pct, 2) }),
     marca("B", a.orizontal.tip_armare)],
    ["struct.lam.rol_agrafe",
     `${a.agrafe.nr} × Ø${a.agrafe.phi_mm}`,
     t("struct.lam.unde_agrafe", { n: a.agrafe.per_m2 }),
     marca("E", a.agrafe.tip_armare)],
  ];
  return (
    <div style={{ overflowX: "auto", marginTop: 10 }}>
      <table className="sg-table" data-testid="tabel-bare-lamela">
        <thead>
          <tr>
            <th>{t("struct.lam.th_rol")}</th>
            <th>{t("struct.lam.th_bare")}</th>
            <th>{t("struct.lam.th_unde")}</th>
            <th>{t("struct.lam.th_marca")}</th>
          </tr>
        </thead>
        <tbody>
          {randuri.map(([rol, bare, unde, m]) => (
            <tr key={rol}>
              <td>{t(rol)}</td>
              <td className="sg-mono">{bare}</td>
              <td className="sg-muted">{unde}</td>
              <td className="sg-mono">{m}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
